import React from 'react';

/**
 * Render a table-column item as a two-line dropdown title: a small
 * source label above the column's name.
 *
 * Exists so the NiceDropdown API can stay "title is a ReactNode" —
 * callers wrap their column items with this helper right before handing
 * them to the dropdown and nothing about sub-labels leaks into the
 * dropdown component itself.
 *
 * Falls back to a plain string title when the column has no source
 * label (keeps the dropdown rows single-line for those cases).
 *
 * @param {{ title: string, subLabel?: string }} column
 * @returns {React.ReactNode}
 */
export function DropdownColumnTitle({ title, subLabel }) {
    if (!subLabel) return title;
    return (
        // Each line clips itself, and every `block` is load-bearing for that. An
        // inline-block wrapper is one atomic box, so the browser cannot clip *inside* it and
        // replaces a long title with a bare "…" — nothing left to read, which is the
        // PFOX-1406 complaint. Blocks fill their container instead of sizing to their text,
        // so nothing overflows the wrapper and each line truncates on its own.
        //
        // `ellipsis-with-hover` over plain `ellipsis` so a truncated title stays readable:
        // hovering the line itself drops the `nowrap` and wraps the full text.
        <span className="block py-1">
            <span className="block ellipsis-with-hover color-text-secondary fs-10 font-medium tracking-wide leading-none">
                {subLabel}
            </span>
            <span className="block ellipsis-with-hover medium leading-6">{title}</span>
        </span>
    );
}

/**
 * Map a flat column list into NiceDropdown-ready items by replacing each
 * item's string `title` with a ReactNode title.
 *
 * Preserves every other field so onReorder / onChange handlers receive
 * the same `id` / `type` / `panelPropertyId` / `checked` metadata they
 * would have without the transformation.
 *
 * @param {Array<{ title: string, subLabel?: string }>} columns
 */
export function withDropdownTitles(columns) {
    return columns.map(c => ({
        ...c,
        // NiceDropdown's search compares against `item.title`, but after this
        // transform `title` becomes a ReactNode (JSX), which can't be substring-
        // matched. `searchTitle` is a plain-string fallback that NiceDropdown
        // checks when `title` isn't a string, so search keeps working.
        searchTitle: c.title,
        title: <DropdownColumnTitle title={c.title} subLabel={c.subLabel} />
    }));
}
